import { EarningsItemWithEntity } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { groupEntriesByDate } from '@terminal/hooks/utils/grouping-utils';
import { useEarningsFilter } from '@terminal/react-query-hooks/earnings/useEarningsFilter';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useEarningsInsightsByEarningsId } from '@terminal/react-query-hooks/insights/useEarningsInsightsByEarningsId';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { EarningsDrawer } from '@terminal/views/theme-overview/tab-content/earnings/components/earnings-drawer/EarningsDrawer';
import { EarningsTable } from '@terminal/views/theme-overview/tab-content/earnings/components/earnings-table/EarningsTable';
import { getColumns } from '@terminal/views/theme-overview/tab-content/earnings/components/earnings-table/utils/getColumnUtils';
import {
  getFiscalDateInfo,
  groupUpcomingByWeeks,
  parseEarnings,
} from '@terminal/views/theme-overview/tab-content/earnings/utils/utils';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MSEarnings.module.scss';

export type EarningsTableItem = EarningsItemWithEntity & {
  isSupported: boolean;
};

export const MSEarnings = ({ theme }: { theme: any }) => {
  const { t } = useTranslation([
    'widget',
    'calendar',
    'earnings',
    'common',
    'chart',
  ]);
  const [drawerData, setDrawerData] = useState<EarningsTableItem | undefined>();
  const { deleteQueryParam, setQueryParams } = useQueryParams();
  const { openDrawer } = useEarningsInsightDrawer();

  const { quarter, year, startDate, endDate } = getFiscalDateInfo();
  const entityTags = theme.constituents.map(
    (constituent: any) => constituent.entity_tag
  );

  const { data: entitiesResult, isLoading: isEntitiesLoading } = useEntities({
    entityTags,
  });
  // this is required to not continuously fetch earnings filter as entities are fetched in useEntities
  // and eventually make a single fetch of earnings filter
  const foundEntityTags = isEntitiesLoading
    ? []
    : entitiesResult.map(e => e.tag);

  const {
    data: earningsQueryResult,
    isError: isEarningsError,
    isFetching: isEarningsFetching,
    error: earningsError,
  } = useEarningsFilter({
    body: {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      entities: foundEntityTags,
    },
  });

  const earningsData = useMemo(
    () =>
      earningsQueryResult?.pages.flatMap(page => page) ??
      ([] as EarningsItemWithEntity[]),
    [earningsQueryResult?.pages]
  );

  const { earningsInsightByEarningsId, isEarningsInsightsFetching } =
    useEarningsInsightsByEarningsId({
      earningsIds: earningsData.map(earning => earning.id),
    });

  const groupedEarningsData = groupEntriesByDate<EarningsItemWithEntity>(
    earningsData,
    'reporting_date'
  );

  const columns = useMemo(
    () =>
      getColumns({
        t,
        earningsInsightByEarningsId,
        isEarningsInsightsFetching,
      }),
    [t, earningsInsightByEarningsId, isEarningsInsightsFetching]
  );

  const { tableData, unsupportedEntityTagsCount } = useMemo(() => {
    let unsupportedEntityTagsCount = 0;
    const tableData = entitiesResult.map((entity, idx) => {
      const earningsItem = earningsData.find(
        item => item.entity_tag === entity.tag
      );

      if (!earningsItem) {
        unsupportedEntityTagsCount++;
      }

      return {
        entity_tag: entity.tag,
        eps: {},
        revenue: {},
        rank: idx + 1,
        ...earningsItem,
        isSupported: !!earningsItem,
      } as EarningsTableItem;
    });
    return { tableData, unsupportedEntityTagsCount };
  }, [entitiesResult, earningsData]);

  const onRowClick = useCallback(
    async (rowData: EarningsTableItem) => {
      const { id, entity_tag: entityTag } = rowData;
      const earningsInsight = earningsInsightByEarningsId.get(id);

      if (earningsInsight) {
        openDrawer({
          entityTag,
          earningsId: id,
          subtab:
            earningsInsight.type === 'preview'
              ? SubTabsType.Preview
              : SubTabsType.Recap,
          source: POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
        });
      } else if (rowData.isSupported) {
        setDrawerData(rowData);
        setQueryParams(
          {
            [queryKeys.activeDrawer]: queryValues.earningsDrawer,
          },
          { replace: true }
        );
      }
    },
    [setDrawerData, setQueryParams, earningsInsightByEarningsId]
  );

  if (isEarningsFetching || isEntitiesLoading) {
    return (
      <SkeletonLoader
        className={styles.skeletonLoaderWrapper}
        areas={[
          {
            styles: {
              width: '1280px',
              borderRadius: '8px',
              height: '100%',
              margin: '20px auto',
            },
          },
        ]}
        data-testid="earnings-loading"
      />
    );
  }

  if (isEarningsError) {
    throw new Error(earningsError?.message);
  }

  const { upcomingEarnings, totalUpcomingCount, totalReportedCount } =
    parseEarnings(groupedEarningsData);
  const buckets = groupUpcomingByWeeks({ upcoming: upcomingEarnings, t });

  const onDrawerClose = () => {
    deleteQueryParam([queryKeys.activeDrawer], { replace: true });
    setDrawerData(undefined);
  };

  return (
    <div className={styles.root}>
      <EarningsDrawer drawerData={drawerData} onDrawerClose={onDrawerClose} />
      <div className={styles.earningsPeriodWrapper}>
        <div className={styles.leftWrapper}>
          <div className={styles.reportedPeriodTitle}>
            {t('widget:themeOverview.tabs.earnings.reportingPeriod', {
              period: `${quarter} ${year}`,
            })}
          </div>
          <div className={styles.flexRow}>
            <div className={styles.earningsCountLabel}>
              {t('widget:themeOverview.tabs.earnings.countOfTotal', {
                count: totalReportedCount,
                total: totalUpcomingCount + totalReportedCount,
              })}
            </div>
            <span className={styles.styledNonBoldText}>
              {t('widget:themeOverview.tabs.earnings.companiesHaveReported')}
            </span>
            {unsupportedEntityTagsCount > 0 && (
              <div className={styles.softBodySmallText}>
                {t('widget:themeOverview.tabs.earnings.noData', {
                  count: unsupportedEntityTagsCount,
                })}
              </div>
            )}
          </div>
        </div>
        <div className={styles.rightWrapper}>
          {buckets.map(({ label, count }) => (
            <div className={styles.bucketWrapper} key={label}>
              <div className={styles.dateAndCountWrapper}>
                <div className={styles.softBodySmallText}>{label}</div>
                <div className={styles.countLabel}>
                  {t('widget:themeOverview.tabs.earnings.companiesCount', {
                    count,
                  })}
                </div>
              </div>
              <div className={styles.verticalDivider} />
            </div>
          ))}
        </div>
      </div>
      <EarningsTable
        tableData={tableData}
        columns={columns}
        onRowClick={onRowClick}
      />
    </div>
  );
};
